import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Metric } from '../types';
import { metricsApi } from '../services/api';
import { extractApiError } from '../types/apiError';
import { HubModal, HubTextField } from './hub/HubModal';
import { HubButton, HubDateField, Diamond, FONT_BODY } from './clients/hub/designSystem';

interface AddValueDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  metric: Metric | null;
  defaultDate?: string;
  /** When editing a specific week, pass the current value so the user replaces instead of adding. */
  defaultValue?: number;
}

/** Specimen of the Phase 4B dialog sweep — the first dialog on the HubModal shell
 *  (docs/design-audit/handoff-master.md §3.1; modal-sweep prototype is the pixel spec). */
const AddValueDialog: React.FC<AddValueDialogProps> = ({ open, onClose, onSuccess, metric, defaultDate, defaultValue }) => {
  const [value, setValue] = useState('');
  const [date, setDate] = useState(defaultDate || new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && metric) {
      setValue(defaultValue !== undefined && defaultValue !== null ? String(defaultValue) : '');
      setDate(defaultDate || new Date().toISOString().split('T')[0]);
      setError(null);
    }
  }, [open, metric, defaultDate, defaultValue]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!metric) return;

    setLoading(true);
    setError(null);

    try {
      const parsedValue = parseFloat(value);
      if (isNaN(parsedValue)) {
        setError('Please enter a valid number');
        setLoading(false);
        return;
      }

      await metricsApi.addValue(metric.id, parsedValue, date);

      // Close dialog first, then refresh
      onClose();

      // Call onSuccess after a small delay to ensure dialog is closed
      setTimeout(() => {
        onSuccess();
      }, 100);
    } catch (err: any) {
      console.error('Error adding metric value:', err);
      const errorMessage = extractApiError(err, 'Failed to add metric value. Please try again.');
      setError(errorMessage);
      setLoading(false);
    }
  };

  if (!metric) return null;

  return (
    <HubModal
      open={open}
      onClose={onClose}
      title={defaultValue !== undefined && defaultValue !== null ? 'Edit Value' : 'Add Value'}
      crumb={metric.name}
      size="sm"
      onSubmit={handleSubmit}
      footer={
        <>
          <HubButton variant="ghost" onClick={loading ? undefined : onClose} disabled={loading}>
            Cancel
          </HubButton>
          <HubButton variant="primary" onClick={() => handleSubmit()} disabled={loading || !value || !date}>
            {defaultValue !== undefined && defaultValue !== null ? 'Save Value' : 'Add Value'}
          </HubButton>
        </>
      }
    >
      <HubTextField
        label="Value"
        type="number"
        value={value}
        onChange={setValue}
        required
        autoFocus
        inputProps={{ step: 'any' }}
      />
      <Box sx={{ mt: 1.9 }}>
        <HubDateField label="Date" value={date} onChange={(next) => setDate(next || '')} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', height: 19, flex: 'none' }}>
          <Diamond size={5} color="var(--indigo)" />
        </Box>
        <Typography sx={{ fontSize: 12.5, color: 'var(--ink-faint)', lineHeight: 1.5, fontFamily: FONT_BODY }}>
          Values are grouped by week (Monday to Sunday). Any date within a week will be associated with that week's Monday.
        </Typography>
      </Box>
      {error && (
        <Box sx={{ mt: 1.5, px: 1.5, py: 1, border: '1px solid var(--paprika)', bgcolor: 'var(--paprika-soft)', borderRadius: '4px' }}>
          <Typography sx={{ fontSize: 12.5, color: 'var(--paprika-on-soft)', fontFamily: FONT_BODY, lineHeight: 1.45 }}>{error}</Typography>
        </Box>
      )}
    </HubModal>
  );
};

export default AddValueDialog;
